import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useMemo } from "react";
import { message } from "antd";
import CustomModal from "../../../../components/Common/CustomModal";
import { StudentData } from "../../../../types/classTypes";
import CommonMultiSelectWithSearch from "components/Common/CommonMultiSelectWithSearch";

interface AssignStudentsModalProps {
  classId: string | null;
  isOpen: boolean;
  allStudents: StudentData[];
  assignedStudents: StudentData[];
  onClose: () => void;
  onSave: (classId: string, selectedStudents: StudentData[]) => Promise<void>;
}

const AssignStudentsModal: React.FC<AssignStudentsModalProps> = ({
  classId,
  isOpen,
  allStudents,
  assignedStudents,
  onClose,
  onSave,
}) => {
  const [currentSelectedStudents, setCurrentSelectedStudents] = useState<StudentData[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    setCurrentSelectedStudents(assignedStudents);
  }, [assignedStudents, classId]);

  const studentOptions = useMemo(
    () =>
      allStudents.map((s) => ({
        value: s.id,
        label: `${s.user.firstName} ${s.user.lastName} ${s.class?.name ? `(${s.class?.name} - ${t(s.class?.classLevel?.name || "")})` : ""}`,
      })),
    [allStudents, t]
  );

  const handleSave = async () => {
    if (!classId) {
      message.error(t("invalid_class_id_please_try_again"));
      return;
    }
    try {
      await onSave(classId, currentSelectedStudents);
      message.success(t("students_assigned_successfully"));
      onClose();
    } catch {
      message.error(t("failed_to_assign_students"));
    }
  };

  return (
    <CustomModal
      title={t("assign_students_to_class")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      saveText={t("assign_students")}
    >
      <CommonMultiSelectWithSearch
        options={studentOptions}
        placeholder={t("select_students")}
        value={currentSelectedStudents.map((s) => s.id)}
        onChange={(ids: string[]) => {
          const selected = ids
            .map((id: string) => allStudents.find((s) => s.id === id))
            .filter(Boolean) as StudentData[];
          setCurrentSelectedStudents(selected);
        }}
        minSearchChars={2}  
        limit={200}
      />
    </CustomModal>
  );
};

export default AssignStudentsModal;
